import { urlConfig } from "src/utils/urlConfig";
import {
  assert,
  is,
  type BaseIssue,
  type BaseSchema,
  type InferInput,
} from "valibot";
import { captureMessage } from "@sentry/react";
import { ApiError } from "./ApiError";

interface FetcherOptions<
  TSchema extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
> extends RequestInit {
  schema?: TSchema;
  baseUrl?: string;
  query?: Record<string, string | number | boolean | undefined>;
}

export async function fetcher<
  TSchema extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(
  path: string,
  options: FetcherOptions<TSchema>,
): Promise<InferInput<TSchema>> {
  const { baseUrl, query, schema, ...requestInit } = options;
  const urlBuilder = new URL(path, baseUrl ?? urlConfig.apiHost);

  // All our APIs require the API key to be passed as a query parameter.
  urlBuilder.searchParams.set("key", import.meta.env.VITE_API_KEY);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        urlBuilder.searchParams.set(key, value.toString());
      }
    }
  }
  const url = urlBuilder.href;

  const response = await fetch(url, {
    ...requestInit,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new ApiError(response.status, message);
  }

  if (!schema) {
    return;
  }

  const contentType = response.headers.get("Content-Type");
  if (contentType && !contentType.includes("application/json")) {
    const message = await response.text();
    throw new ApiError(
      response.status,
      `Expected JSON response, got ${response.headers.get(
        "content-type",
      )}. Response: ${message}`,
    );
  }

  const responseJson = await response.json();
  if (import.meta.env.DEV) {
    // In development mode, we want to validate and throw an exception
    // if the response does not match the expected schema so we can catch
    // schema violations early.
    assert(schema, responseJson);
  } else {
    // In production mode, we want to log the error but not throw an exception
    // so that we don't crash the app.
    if (!is(schema, responseJson)) {
      captureMessage("API Schema Violation: " + url);
      return responseJson as InferInput<TSchema>;
    }
  }

  return responseJson;
}
